import { useMutation, useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  type ListsPreviewResponse,
  type QuoteLookups,
  type QuotePriceList,
  type QuoteResponse,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';

function ListName({ list }: { list: QuotePriceList }) {
  return (
    <>
      <strong>{list.name}</strong>
      {list.fromClient && <span className="pill info">dal cliente</span>}
    </>
  );
}

/**
 * The price lists of a quote (proposal Listini §5): from its client, the
 * Base without one, and «Cambia listino…» while it is a draft.
 */
export function PriceListsBar({
  quote,
  lookups,
  canChange,
  onChanged,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  canChange: boolean;
  onChanged: (updated: QuoteResponse) => Promise<void>;
}) {
  const [changing, setChanging] = useState(false);
  return (
    <div className="price-lists-bar">
      <span className="muted">Listino attività</span> <ListName list={quote.priceLists.activity} />
      {quote.priceLists.cost && (
        <>
          <span className="muted">Listino passività</span> <ListName list={quote.priceLists.cost} />
        </>
      )}
      {canChange && (
        <button type="button" className="small" onClick={() => setChanging(true)}>
          Cambia listino…
        </button>
      )}
      {changing && (
        <ChangeListsDialog
          quote={quote}
          lookups={lookups}
          onClose={() => setChanging(false)}
          onDone={async (updated) => {
            setChanging(false);
            await onChanged(updated);
          }}
        />
      )}
    </div>
  );
}

/**
 * The new lists, and the prices that would move with them: the lines with a
 * price written by hand start unticked (proposal Listini §5).
 */
function ChangeListsDialog({
  quote,
  lookups,
  onClose,
  onDone,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  onClose: () => void;
  onDone: (updated: QuoteResponse) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [activity, setActivity] = useState(quote.priceLists.activity.code ?? '');
  const [cost, setCost] = useState(quote.priceLists.cost?.code ?? '');
  const [lines, setLines] = useState<Set<string>>(new Set());
  const [costs, setCosts] = useState<Set<string>>(new Set());
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const query = new URLSearchParams({ activityListCode: activity, costListCode: cost });
  const preview = useQuery({
    queryKey: ['quotes', quote.id, 'lists', activity, cost],
    queryFn: () =>
      apiRequest<ListsPreviewResponse>(`/quotes/${quote.id}/lists/preview?${query.toString()}`),
  });
  // What moves starts ticked, but a price written by hand.
  useEffect(() => {
    if (!preview.data) return;
    setLines(new Set(preview.data.lines.filter((line) => !line.manual).map((line) => line.lineId)));
    setCosts(new Set(preview.data.costs.map((item) => item.costId)));
  }, [preview.data]);
  const apply = useMutation({
    mutationFn: (reprice: boolean) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/lists`, {
        method: 'POST',
        body: {
          activityListCode: activity || null,
          costListCode: cost || null,
          lines: reprice ? [...lines] : [],
          costs: reprice ? [...costs] : [],
        },
      }),
    onSuccess: onDone,
  });
  const toggle = (set: Set<string>, id: string, update: (next: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    update(next);
  };
  const options = (kind: 'Attività' | 'Passività') =>
    lookups.priceLists.filter((list) => list.kind === kind && !list.code.startsWith('BASE-'));
  const moving = (preview.data?.lines.length ?? 0) + (preview.data?.costs.length ?? 0);
  const ticked = lines.size + costs.size;

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Cambia listino</h2>
        <div className="form-grid">
          <Field label="Listino attività">
            <select value={activity} onChange={(event) => setActivity(event.target.value)}>
              <option value="">Base</option>
              {options('Attività').map((list) => (
                <option key={list.code} value={list.code}>
                  {list.description}
                </option>
              ))}
            </select>
          </Field>
          {quote.priceLists.cost && (
            <Field label="Listino passività">
              <select value={cost} onChange={(event) => setCost(event.target.value)}>
                <option value="">Base</option>
                {options('Passività').map((list) => (
                  <option key={list.code} value={list.code}>
                    {list.description}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
        {preview.isPending && <p className="muted">Calcolo dei prezzi…</p>}
        {preview.error && <p className="error">{preview.error.message}</p>}
        {preview.data && moving === 0 && (
          <p className="notice info">Con questi listini nessun prezzo cambia.</p>
        )}
        {preview.data && moving > 0 && (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th />
                  <th>Riga o costo</th>
                  <th className="right">Ora</th>
                  <th className="right">Con il nuovo listino</th>
                </tr>
              </thead>
              <tbody>
                {preview.data.lines.map((line) => (
                  <tr key={line.lineId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Aggiorna ${line.label}`}
                        checked={lines.has(line.lineId)}
                        onChange={() => toggle(lines, line.lineId, setLines)}
                      />
                    </td>
                    <td>
                      {line.label}{' '}
                      {line.manual && <span className="pill info">scritto a mano</span>}
                    </td>
                    <td className="right">{formatMoney(line.current)}</td>
                    <td className="right">{line.proposed ? formatMoney(line.proposed) : '—'}</td>
                  </tr>
                ))}
                {preview.data.costs.map((item) => (
                  <tr key={item.costId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Aggiorna il costo ${item.label}`}
                        checked={costs.has(item.costId)}
                        onChange={() => toggle(costs, item.costId, setCosts)}
                      />
                    </td>
                    <td>Costo · {item.label}</td>
                    <td className="right">{formatMoney(item.current)}</td>
                    <td className="right">{item.proposed ? formatMoney(item.proposed) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">
          Le righe con un prezzo scritto a mano si aggiornano solo se le spunti. Cambiare i prezzi
          toglie lo sconto sul documento, come ogni modifica delle righe.
        </p>
        {apply.error && <p className="error">{apply.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          {moving > 0 && (
            <button type="button" disabled={apply.isPending} onClick={() => apply.mutate(false)}>
              Cambia e lascia i prezzi
            </button>
          )}
          <button
            type="button"
            className="primary"
            disabled={apply.isPending || preview.isPending}
            onClick={() => apply.mutate(true)}
          >
            {moving > 0
              ? `Cambia e aggiorna ${ticked} ${ticked === 1 ? 'prezzo' : 'prezzi'}`
              : 'Cambia listino'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
